import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Check, Pencil, Loader2, Armchair, Link2, Copy, MoreVertical, UserMinus, AlertTriangle, UserX, UserPlus } from 'lucide-react';
import { api, parseApiError, type ClanSeat, type Me } from '../lib/api';
import { cn } from '../lib/cn';
import { ConfirmModal } from './ConfirmModal';
import { HelpText } from './HelpText';
import { canManageTeamScoped } from '../lib/captainAccess';

// 16 → 100 (Lisa, 2026-07-19): big-org clans need room for full member
// rosters, not just a match-night squad. Must match the backend hard cap
// (portalApi/index.ts, "Hard cap: 100 seats per clan").
const MAX_SEATS = 100;

type Toast = { kind: 'removed' | 'role' | 'error'; msg: string; undo?: () => void } | null;

type Props = {
  clanName: string;
  seats: ClanSeat[];
  className?: string;
  me?: Me;
  canManage?: boolean;
  // Delegation-visibility audit (T4) — teamNos the viewer captains. Widens
  // ONLY the "Filled Seats" remove action, per-seat, to a captain of that
  // seat's own team (removeClanMember is the same team-scoped-delegation
  // route ClanMemberRow/ClanTeamsBoard's remove action already hits).
  // Ghost (unclaimed) pool seats below are NEVER captain-eligible — they're
  // clan-wide by definition (no teamNo), same as the backend's own seat-create
  // rule ("pool seats are a clan-wide concept, not a captain's to create").
  viewerCaptainTeams?: number[];
  isClanOwner?: boolean;
  onToast?: (t: Toast) => void;
};

// Imperative handle so the parent's "Add Member" button can jump straight to
// the invite picker (scroll + focus + a brief pulse), rather than silently
// scrolling near it.
export type GhostSeatManagerHandle = { focusInvite: () => void };

export const GhostSeatManager = forwardRef<GhostSeatManagerHandle, Props>(function GhostSeatManager(
  { clanName, seats, className, me, canManage = false, viewerCaptainTeams = [], isClanOwner = false, onToast },
  ref,
) {
  const qc = useQueryClient();
  const discordInputRef = useRef<HTMLInputElement>(null);
  const [highlightInvite, setHighlightInvite] = useState(false);

  useImperativeHandle(ref, () => ({
    focusInvite() {
      const el = discordInputRef.current;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
      setHighlightInvite(true);
      window.setTimeout(() => setHighlightInvite(false), 1600);
    },
  }));
  const [editingGt, setEditingGt] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [linkingGt, setLinkingGt] = useState<string | null>(null);
  const [linkTarget, setLinkTarget] = useState('');
  const [mintedLink, setMintedLink] = useState<{ gamertag: string; url: string; dmSent: boolean; shareable?: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareCopiedGt, setShareCopiedGt] = useState<string | null>(null);
  const [discordQuery, setDiscordQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [inviteResult, setInviteResult] = useState<{ name: string; dmed: boolean; link?: string } | null>(null);

  const refresh = () => {
    // Invalidate ALL clan queries — the parent keys its clan query by me.clanName
    // (canonical slug), which can differ in case from the clanName prop here.
    qc.invalidateQueries({ queryKey: ['clan'] });
  };

  const deleteMut = useMutation({
    mutationFn: (gamertag: string) => api.deleteGhostSeat(clanName, gamertag),
    onSuccess: refresh,
    // Structured backend errors (e.g. roster_locked from the start-date
    // lifecycle gate) come back double-JSON-encoded — unwrap to the human
    // message; parseApiError degrades to the plain string for ordinary errors.
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const renameMut = useMutation({
    mutationFn: ({ oldGt, newGt }: { oldGt: string; newGt: string }) =>
      api.renameGhostSeat(clanName, oldGt, newGt),
    onSuccess: () => { setEditingGt(null); setEditValue(''); setError(''); refresh(); },
    // Structured backend errors (e.g. roster_locked from the start-date
    // lifecycle gate) come back double-JSON-encoded — unwrap to the human
    // message; parseApiError degrades to the plain string for ordinary errors.
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const claimLinkMut = useMutation({
    mutationFn: ({ gamertag, targetDiscordId }: { gamertag: string; targetDiscordId: string }) =>
      api.mintSeatClaimLink(clanName, gamertag, targetDiscordId),
    onSuccess: (res, vars) => {
      setMintedLink({ gamertag: vars.gamertag, url: res.url, dmSent: res.dmSent });
      setLinkingGt(null); setLinkTarget(''); setError(''); setCopied(false);
    },
    // Structured backend errors (e.g. roster_locked from the start-date
    // lifecycle gate) come back double-JSON-encoded — unwrap to the human
    // message; parseApiError degrades to the plain string for ordinary errors.
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Open shareable link — mint a 24h claim link and copy it. The leader sends it
  // however they like; no Discord id needed.
  const shareLinkMut = useMutation({
    mutationFn: (gamertag: string) => api.mintShareableSeatLink(clanName, gamertag),
    onSuccess: (res, gamertag) => {
      // Show the link ON SCREEN (mobile browsers often block silent clipboard
      // writes) and best-effort auto-copy on top.
      setMintedLink({ gamertag, url: res.url, dmSent: false, shareable: true });
      setLinkingGt(null); setError(''); setCopied(false);
      navigator.clipboard.writeText(res.url)
        .then(() => { setCopied(true); setShareCopiedGt(gamertag); setTimeout(() => setShareCopiedGt(null), 2500); })
        .catch(() => {});
    },
    // Structured backend errors (e.g. roster_locked from the start-date
    // lifecycle gate) come back double-JSON-encoded — unwrap to the human
    // message; parseApiError degrades to the plain string for ordinary errors.
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Discord @person invite — debounce the query, search the clan's bound guild,
  // invite the picked member (bot DMs them an Accept button; link fallback).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(discordQuery.trim().replace(/^@+/, '')), 300);
    return () => clearTimeout(t);
  }, [discordQuery]);

  const memberSearch = useQuery({
    queryKey: ['discordMembers', clanName, debouncedQuery],
    queryFn: () => api.searchDiscordMembers(clanName, debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const inviteMut = useMutation({
    mutationFn: ({ discordId, displayName }: { discordId: string; displayName: string }) =>
      api.inviteDiscordMember(clanName, discordId, displayName),
    onSuccess: (res) => {
      setInviteResult({ name: res.displayName, dmed: res.dmed, link: res.link });
      setDiscordQuery(''); setDebouncedQuery(''); setError(''); refresh();
    },
    // Structured backend errors (e.g. roster_locked from the start-date
    // lifecycle gate) come back double-JSON-encoded — unwrap to the human
    // message; parseApiError degrades to the plain string for ordinary errors.
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Seat eviction — Remove / Ban actions on claimed seats
  const chipMenuRef = useRef<HTMLDivElement>(null);
  const [openSeatMenuId, setOpenSeatMenuId] = useState<string | null>(null);
  const [confirmRemoveSeat, setConfirmRemoveSeat] = useState<ClanSeat | null>(null);
  const [confirmBanSeat, setConfirmBanSeat] = useState<ClanSeat | null>(null);
  const [seatBanReason, setSeatBanReason] = useState('');

  useEffect(() => {
    if (!openSeatMenuId) return;
    const handler = (e: MouseEvent) => {
      if (!chipMenuRef.current?.contains(e.target as Node)) setOpenSeatMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openSeatMenuId]);

  const removeSeatMut = useMutation({
    mutationFn: (discordId: string) => api.removeClanMember(clanName, discordId),
    onSuccess: () => {
      refresh();
      setConfirmRemoveSeat(null);
      onToast?.({ kind: 'removed', msg: `Member removed from ${clanName}.` });
    },
    onError: (e: Error) => onToast?.({ kind: 'error', msg: parseApiError(e).message }),
  });

  const banSeatMut = useMutation({
    mutationFn: ({ discordId, reason }: { discordId: string; reason?: string }) =>
      api.banClanMember(clanName, discordId, reason),
    onSuccess: () => {
      refresh();
      setConfirmBanSeat(null);
      setSeatBanReason('');
      onToast?.({ kind: 'removed', msg: `Member banned from ${clanName}.` });
    },
    onError: (e: Error) => onToast?.({ kind: 'error', msg: parseApiError(e).message }),
  });

  // Team-reserved ghosts (teamNo set) appear on their team card in the Squad
  // Board instead — this list is only the unreserved (pool) ghost seats.
  const ghostSeats = seats.filter(s => s.status === 'GHOST' && s.teamNo == null);
  const claimedSeats = seats.filter(s => s.status === 'CLAIMED');
  const pendingSeats = seats.filter(s => s.status === 'PENDING_CLAIM');
  const atCapacity = seats.length >= MAX_SEATS;

  const startEdit = (gt: string) => {
    setEditingGt(gt);
    setEditValue(gt);
    setError('');
  };

  const submitRename = (oldGt: string) => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === oldGt) { setEditingGt(null); return; }
    renameMut.mutate({ oldGt, newGt: trimmed });
  };

  return (
    <div className={cn('rounded-xl bg-paper-elevated p-5 shadow-elev1', className)}>
      <div className="mb-4 flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
        <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          Add Members
        </h2>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-iron-500">
          {seats.length} of {MAX_SEATS} seats
        </span>
      </div>

      <HelpText>
        <p className="mb-4 text-xs lg:text-sm text-iron-400">
          Bring players into the clan. Fastest is to <b>invite them from your Discord server</b> below — the bot DMs them an Accept. Not on Discord yet? <b>Reserve a seat</b> with their gamertag and send a claim link. Players can also ask to join from the clan's public page — approve them under <b>Applications</b> above. Each member takes a <b>seat</b> (a roster spot); once they're in, assign them to teams in the Squad Board below.
        </p>
      </HelpText>

      {/* Invite from Discord — @person picker. This is the primary "add a
          member" path; the parent's Add Member button focuses this input and
          briefly pulses this block via the focusInvite() handle.
          Gated on canManage (Lisa, 2026-07-19): inviting is a clan-wide
          action the backend only permits owner/co-admin (the delegation
          overlay never affirms it) — rendering it for every member left
          plain members clicking an invite that silently 403s. */}
      {canManage && (
      <div className={cn('mb-4 rounded-lg transition-all duration-300', highlightInvite && 'ring-2 ring-forge-500 -m-3 p-3')}>
        <p className="mb-1.5 font-display text-[10px] font-black uppercase tracking-[0.2em] text-forge-300">
          Invite from Discord
        </p>
        <HelpText><p className="mb-2 text-[11px] lg:text-xs text-iron-500">Search your <b>Discord server</b> and pick a teammate — the bot DMs them an <b>Accept</b> button.</p></HelpText>
        <div className="relative">
          <input
            ref={discordInputRef}
            value={discordQuery}
            onChange={e => { setDiscordQuery(e.target.value); setInviteResult(null); }}
            placeholder="Type a teammate's name (no @ needed)…"
            disabled={atCapacity || inviteMut.isPending}
            className="input w-full border border-iron-600"
          />
          {debouncedQuery.length >= 2 && (
            <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg bg-iron-800 shadow-elev2">
              {memberSearch.isFetching ? (
                <p className="px-3 py-2 text-xs text-iron-400">Searching…</p>
              ) : memberSearch.isError ? (
                /* Surface the REAL failure instead of masking it as "no one
                   by that name" (Lisa, 2026-07-19): an org-less clan 409s
                   ("no organisation, so no Discord server to invite from"),
                   a missing Members Intent 403s — both used to render as a
                   not-found, sending leaders spelling-checking a name that
                   was never searchable in the first place. */
                <p className="px-3 py-2 text-xs text-amber-300/90">
                  {parseApiError(memberSearch.error).message}
                </p>
              ) : (memberSearch.data?.members.length ?? 0) === 0 ? (
                <p className="px-3 py-2 text-xs text-iron-500">
                  No one by that name in your server — check the spelling, or they may not have joined yet.
                </p>
              ) : (
                memberSearch.data!.members.map(m => (
                  <button
                    key={m.discordId}
                    onClick={() => inviteMut.mutate({ discordId: m.discordId, displayName: m.displayName })}
                    disabled={inviteMut.isPending}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-iron-700 disabled:opacity-50"
                  >
                    {m.avatar ? (
                      <img src={`https://cdn.discordapp.com/avatars/${m.discordId}/${m.avatar}.png?size=32`} alt="" className="h-6 w-6 rounded-full" />
                    ) : (
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-iron-600 text-[9px] font-bold text-iron-200">
                        {m.displayName.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <span className="flex-1 truncate text-sm lg:text-base text-iron-100">{m.displayName}</span>
                    <span className="font-display text-[9px] font-black uppercase tracking-wider text-forge-300">Invite</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        {inviteResult && (
          <div className="mt-2 rounded-lg bg-emerald-950/30 px-3 py-2 text-xs lg:text-sm text-emerald-200">
            {inviteResult.dmed ? (
              <>✅ Invited <b>{inviteResult.name}</b> — they've got a DM with an Accept button.</>
            ) : (
              <>
                ⚠️ Couldn't DM <b>{inviteResult.name}</b> (their DMs are closed). Send them this link — it fills
                their seat (expires in 24h):<br />
                <a href={inviteResult.link} className="break-all text-forge-300 underline">{inviteResult.link}</a>
              </>
            )}
          </div>
        )}
      </div>
      )}

      {atCapacity && (
        <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-amber-400">
          Clan at seat cap ({MAX_SEATS}). Remove an unclaimed ghost to free a slot.
        </p>
      )}

      {error && (
        <p className="mb-3 rounded-lg bg-red-950/50 px-3 py-2 text-xs text-red-300">
          {error}
        </p>
      )}

      {/* Ghost seats list */}
      {ghostSeats.length === 0 ? (
        <p className="text-sm lg:text-base text-iron-500">No unreserved ghost seats. Invite from Discord above, or reserve a seat on a team from the Squad Board.</p>
      ) : (
        <div className="divide-y divide-iron-700/60">
          {ghostSeats.map(seat => {
            const isEditing = editingGt === seat.gamertag;
            const rowBusy = deleteMut.isPending || renameMut.isPending;
            return (
              <div key={seat.gamertag} className="py-2">
              <div
                className="flex flex-wrap items-center gap-2 px-3 py-2"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-iron-700 bg-iron-950">
                  <Armchair className="h-3 w-3 text-iron-400" strokeWidth={2} />
                </span>

                {isEditing ? (
                  <>
                    <input
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') submitRename(seat.gamertag);
                        else if (e.key === 'Escape') { setEditingGt(null); setEditValue(''); }
                      }}
                      className="input flex-1"
                    />
                    <button
                      onClick={() => submitRename(seat.gamertag)}
                      disabled={renameMut.isPending}
                      className="flex h-7 w-7 items-center justify-center border border-emerald-500/50 bg-emerald-950/40 text-emerald-200 transition-colors hover:border-emerald-400 hover:bg-emerald-900/50 disabled:opacity-40"
                      aria-label="Save"
                    >
                      {renameMut.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                      ) : (
                        <Check className="h-3 w-3" strokeWidth={3} />
                      )}
                    </button>
                    <button
                      onClick={() => { setEditingGt(null); setEditValue(''); }}
                      className="flex h-7 w-7 items-center justify-center border border-iron-700 text-iron-400 transition-colors hover:border-iron-500 hover:text-iron-200"
                      aria-label="Cancel"
                    >
                      <X className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate font-display text-sm lg:text-base font-black uppercase tracking-wide text-white">
                      {seat.gamertag || '(no gamertag)'}
                    </span>
                    <span className="shrink-0 rounded-full bg-iron-800 px-2.5 py-1 font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-400">
                      Ghost
                    </span>
                    <div className="mt-1 flex w-full flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => shareLinkMut.mutate(seat.gamertag)}
                      disabled={rowBusy || shareLinkMut.isPending}
                      className="flex shrink-0 items-center gap-1 rounded-md bg-forge-500/15 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-forge-200 transition-colors hover:bg-forge-500/25 hover:text-white disabled:opacity-40"
                      title="Copy a 24-hour claim link to send this player however you like"
                    >
                      {shareLinkMut.isPending && shareLinkMut.variables === seat.gamertag
                        ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                        : shareCopiedGt === seat.gamertag
                          ? <Check className="h-3 w-3" strokeWidth={2.5} />
                          : <Copy className="h-3 w-3" strokeWidth={2.5} />}
                      {shareCopiedGt === seat.gamertag ? 'Copied' : 'Copy link'}
                    </button>
                    <button
                      onClick={() => { setLinkingGt(linkingGt === seat.gamertag ? null : seat.gamertag); setLinkTarget(''); setMintedLink(null); }}
                      disabled={rowBusy}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-iron-400 transition-colors hover:bg-iron-800 hover:text-forge-200 disabled:opacity-40"
                      aria-label="Send one-click claim link"
                      title="Send a one-click claim link via Discord DM"
                    >
                      <Link2 className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={() => startEdit(seat.gamertag)}
                      disabled={rowBusy}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-iron-400 transition-colors hover:bg-iron-800 hover:text-forge-200 disabled:opacity-40"
                      aria-label="Rename seat"
                      title="Rename (typo fix)"
                    >
                      <Pencil className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove ghost seat for "${seat.gamertag}"? The gamertag becomes free to re-add later.`)) {
                          deleteMut.mutate(seat.gamertag);
                        }
                      }}
                      disabled={rowBusy}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-iron-400 transition-colors hover:bg-red-950/50 hover:text-red-300 disabled:opacity-40"
                      aria-label="Delete seat"
                      title="Delete ghost seat"
                    >
                      {deleteMut.isPending && deleteMut.variables === seat.gamertag ? (
                        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                      ) : (
                        <X className="h-3 w-3" strokeWidth={2.5} />
                      )}
                    </button>
                    </div>
                  </>
                )}
              </div>
              {linkingGt === seat.gamertag && (
                <div className="border-t border-iron-800 px-3 py-2">
                  <p className="mb-1.5 text-[10px] uppercase tracking-wider text-iron-500">
                    One-click claim link — their tap claims the seat, no approval step
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={linkTarget}
                      onChange={e => setLinkTarget(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && /^\d{17,20}$/.test(linkTarget.trim())) {
                          claimLinkMut.mutate({ gamertag: seat.gamertag, targetDiscordId: linkTarget.trim() });
                        } else if (e.key === 'Escape') { setLinkingGt(null); }
                      }}
                      placeholder="Their Discord user ID (17–20 digits)"
                      className="input flex-1"
                    />
                    <button
                      onClick={() => claimLinkMut.mutate({ gamertag: seat.gamertag, targetDiscordId: linkTarget.trim() })}
                      disabled={!/^\d{17,20}$/.test(linkTarget.trim()) || claimLinkMut.isPending}
                      className="flex shrink-0 items-center gap-1.5 border border-forge-500/60 bg-forge-950/70 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-forge-100 transition-all hover:border-forge-400 hover:text-white disabled:border-iron-700 disabled:text-iron-500"
                    >
                      {claimLinkMut.isPending
                        ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                        : <Link2 className="h-3 w-3" strokeWidth={2.5} />}
                      Send link
                    </button>
                  </div>
                </div>
              )}
              {mintedLink?.gamertag === seat.gamertag && (
                <div className="border-t border-emerald-500/30 bg-emerald-950/20 px-3 py-2">
                  <p className="text-xs lg:text-sm text-emerald-300">
                    {mintedLink.shareable
                      ? '24-hour claim link — copy it and send it to your player however you like:'
                      : mintedLink.dmSent
                        ? '✓ Claim link DM\'d — one tap and they\'re in.'
                        : 'Link minted, but the DM failed (closed DMs?). Copy it and send it yourself:'}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate font-mono text-[10px] text-iron-400">{mintedLink.url}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(mintedLink.url).then(() => setCopied(true)).catch(() => {}); }}
                      className="flex shrink-0 items-center gap-1 border border-iron-700 px-2 py-1 text-[10px] uppercase tracking-wider text-iron-300 hover:border-iron-500 hover:text-white"
                    >
                      {copied ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <Copy className="h-3 w-3" strokeWidth={2.5} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      {/* Claimed + pending seats */}
      {(claimedSeats.length > 0 || pendingSeats.length > 0) && (
        <div className="mt-5 border-t border-forge-900/30 pt-4">
          <p className="mb-2 font-display text-[9px] font-black uppercase tracking-[0.22em] text-iron-500">
            Filled Seats
          </p>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {[...claimedSeats, ...pendingSeats].map(seat => {
              const isClaimed = seat.status === 'CLAIMED';
              // Delegation-visibility audit (T4): a captain may remove a
              // CLAIMED seat on their OWN team (this hits the same
              // removeClanMember route ClanMemberRow's remove does) — Ban
              // stays isClanOwner-gated below, untouched.
              const canActOnSeat = isClaimed && !!seat.claimedBy && seat.claimedBy !== me?.discordId
                && canManageTeamScoped(canManage, viewerCaptainTeams, seat.teamNo);
              const isMenuOpen = openSeatMenuId === seat.claimedBy;
              return (
                <div
                  key={seat.gamertag}
                  className={cn(
                    'flex items-center gap-2 border px-2.5 py-1.5',
                    isClaimed
                      ? 'border-emerald-500/30 bg-emerald-950/20'
                      : 'border-amber-500/30 bg-amber-950/20'
                  )}
                  title={isClaimed ? 'Claimed' : 'Pending claim — approve via Discord DM'}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      isClaimed ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate font-display text-xs lg:text-sm font-black uppercase tracking-wide text-white">
                    {seat.gamertag}
                    {isClaimed && seat.claimedByName && seat.claimedByName !== seat.gamertag && (
                      <span className="ml-1 font-sans text-[9px] font-normal normal-case tracking-normal text-iron-500">
                        {seat.claimedByName}
                      </span>
                    )}
                  </span>
                  {canActOnSeat && (
                    <div
                      className="relative ml-auto shrink-0"
                      ref={isMenuOpen ? chipMenuRef : undefined}
                    >
                      <button
                        onClick={() => setOpenSeatMenuId(prev => prev === seat.claimedBy ? null : seat.claimedBy!)}
                        className={cn(
                          'flex h-6 w-6 items-center justify-center rounded-sm border transition-colors',
                          isMenuOpen
                            ? 'border-forge-500/70 bg-iron-800 text-white'
                            : 'border-iron-600/70 bg-iron-950 text-iron-200 hover:border-iron-400 hover:bg-iron-800 hover:text-white active:bg-iron-800'
                        )}
                        aria-label="Member actions"
                        aria-expanded={isMenuOpen}
                      >
                        <MoreVertical className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                      {isMenuOpen && (
                        <div className="absolute right-0 top-full z-40 mt-0.5 w-44 border border-iron-700/60 bg-iron-950 shadow-lg">
                          <div className="border-b border-iron-800/40 px-3 py-1.5">
                            <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
                              Actions
                            </p>
                          </div>
                          <div className="py-1">
                            <button
                              onClick={() => { setOpenSeatMenuId(null); setConfirmRemoveSeat(seat); }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/40 hover:text-red-200"
                            >
                              <UserMinus className="h-3 w-3" strokeWidth={2.5} />
                              Remove from Clan
                            </button>
                            {isClanOwner && (
                              <button
                                onClick={() => { setOpenSeatMenuId(null); setConfirmBanSeat(seat); setSeatBanReason(''); }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-200 transition-colors hover:bg-red-950/60 hover:text-red-100 border-t border-iron-800/40"
                              >
                                <UserX className="h-3 w-3 text-red-400" strokeWidth={2.5} />
                                Ban from Clan
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[10px] text-iron-500">Filled = on the roster · Pending = invited, waiting to accept · Placeholder = a held gamertag, not claimed yet.</p>
        </div>
      )}

      {/* Remove confirm */}
      {confirmRemoveSeat && (
        <ConfirmModal
          tone="danger"
          icon={AlertTriangle}
          title={`Remove ${confirmRemoveSeat.gamertag} from ${clanName}?`}
          body="Their seat becomes open again and can be claimed by someone else."
          confirmLabel="Remove"
          confirmIcon={UserMinus}
          onConfirm={() => removeSeatMut.mutate(confirmRemoveSeat.claimedBy!)}
          onCancel={() => setConfirmRemoveSeat(null)}
          pending={removeSeatMut.isPending}
        />
      )}

      {/* Ban confirm */}
      {confirmBanSeat && (
        <ConfirmModal
          tone="danger"
          icon={UserX}
          title={`Ban ${confirmBanSeat.gamertag} from ${clanName}?`}
          body="They'll be removed immediately and blocked from rejoining. You can lift the ban later."
          confirmLabel="Ban"
          confirmIcon={UserX}
          onConfirm={() => banSeatMut.mutate({ discordId: confirmBanSeat.claimedBy!, reason: seatBanReason.trim() || undefined })}
          onCancel={() => { setConfirmBanSeat(null); setSeatBanReason(''); }}
          pending={banSeatMut.isPending}
        >
          <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
            Reason (optional)
          </label>
          <textarea
            value={seatBanReason}
            onChange={e => setSeatBanReason(e.target.value)}
            placeholder="e.g. repeated unsportsmanlike conduct"
            rows={2}
            className="w-full resize-none border border-iron-700/60 bg-iron-950 px-3 py-2 text-xs text-iron-100 placeholder-iron-600 focus:border-iron-500 focus:outline-none"
          />
        </ConfirmModal>
      )}
    </div>
  );
});
